import { useQuery, keepPreviousData } from '@tanstack/react-query';
import querystring from 'query-string';
import { useCallback, useContext, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { AuthContext, useApiClient, ApiClientService, DEFAULT_DEBOUNCE_TIME } from 'libs.react_internal';
export var SortColumn;
(function (SortColumn) {
    SortColumn["USER_COUNT"] = "userCount";
    SortColumn["STUDY_COUNT"] = "studyCount";
})(SortColumn || (SortColumn = {}));
export const useWorkspaceSites = ({ pageIndex = 0, pageSize = 10 }) => {
    const { entityId } = useContext(AuthContext);
    const momClient = useApiClient(ApiClientService.MOM);
    const [error, setError] = useState();
    const [pagination, setPagination] = useState({ pageSize, pageIndex });
    const [totalPages, setTotalPages] = useState(0);
    const [search, setSearch] = useState('');
    const [noPiiFilter, setNoPiiFilter] = useState(null);
    const [sort, setSort] = useState({ column: null, direction: 'asc' });
    const queryKey = [
        'entities',
        entityId.toString(),
        'sites',
        {
            pageIndex: pagination.pageIndex.toString(),
            pageSize: pagination.pageSize.toString(),
            search,
            noPII: noPiiFilter?.toString() || '',
            sort: sort.column ? `${sort.column}:${sort.direction}` : '',
        },
    ];
    const debouncedSearchChange = useDebouncedCallback((value = '') => {
        setSearch(value);
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }, DEFAULT_DEBOUNCE_TIME);
    const fetchSites = async () => {
        try {
            const queryObject = {
                limit: pagination.pageSize.toString(),
                page: (pagination.pageIndex + 1).toString(),
            };
            if (search) {
                queryObject.search = search;
            }
            if (noPiiFilter !== null) {
                queryObject.noPII = noPiiFilter.toString();
            }
            if (sort.column) {
                queryObject.sort = `${sort.column}:${sort.direction}`;
            }
            const queryParams = querystring.stringify(queryObject);
            const { data: response } = await momClient.get(`/v1/entities/${entityId}/sites?${queryParams}`);
            const totalPages = Math.ceil(response.total / pagination.pageSize);
            setTotalPages(totalPages);
            return response?.data || [];
        }
        catch (error) {
            setError(error);
        }
    };
    const { data: workspaceSites, isLoading } = useQuery({
        queryKey,
        queryFn: fetchSites,
        placeholderData: keepPreviousData,
    });
    const handleSortChange = useCallback((column, direction) => {
        setSort((prev) => ({
            column,
            direction: direction || (prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'),
        }));
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }, []);
    const handleClearSort = useCallback(() => {
        setSort({ column: null, direction: 'asc' });
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }, []);
    const handleFilterChange = useCallback((noPii) => {
        setNoPiiFilter(noPii);
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }, []);
    return {
        isLoading,
        error,
        workspaceSites,
        pagination,
        totalPages,
        sort,
        noPiiFilter,
        onPaginationChange: setPagination,
        onSearchChange: debouncedSearchChange,
        onSortChange: handleSortChange,
        onClearSort: handleClearSort,
        onFilterChange: handleFilterChange,
    };
};
